import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { Icon } from '@/components/ui/icon.js';
import { useAuthStore } from '@/stores/auth.js';
import { storageKey } from '@/lib/storage-key.js';
import { POPUP_QUERY_KEYS, fetchActivePopups, recordPopupImpression } from './api.js';
import { selectPopupsToShow } from './select.js';
import {
  effectiveDayCounts,
  emptyDayCounts,
  parseDayCounts,
  recordShow,
  todayKey,
  type DayCounts,
} from './frequency.js';

function readIds(storage: () => Storage, key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(storage().getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeIds(storage: () => Storage, key: string, ids: string[]): void {
  try {
    storage().setItem(key, JSON.stringify(ids));
  } catch {
    // storage unavailable (private mode) — the rule degrades to this mount only
  }
}

const session = () => sessionStorage;
const device = () => localStorage;

function readDayCounts(key: string, today: string): DayCounts {
  try {
    return parseDayCounts(localStorage.getItem(key), today);
  } catch {
    // Storage can throw outright, not just return null (Safari private mode,
    // an iframe with third-party storage blocked). Uncounted is the safe side:
    // the popup still respects the once-per-session rule.
    return emptyDayCounts(today);
  }
}

/**
 * CMS popups (BO /content) on site entry, whatever page the visit starts on —
 * shown once per browser session, highest priority first, one dialog at a time,
 * and no more times per calendar day than the creative allows. Closing one opens
 * the next. A creative the viewer ticked "don't show again" on stays hidden in
 * this browser for good (localStorage, per device — not per member).
 *
 * The per-day allowance is counted by svc-notify for a signed-in member, so it
 * survives a cache clear, a second device and an incognito window. An anonymous
 * visitor has no id to count by and keeps the per-device counter.
 */
export function LobbyPopups() {
  const { t } = useTranslation('common');
  const authed = Boolean(useAuthStore((s) => s.accessToken));
  const seenKey = storageKey('popups.seen');
  const showsKey = storageKey('popups.shows');
  const hiddenKey = storageKey('popups.hidden');
  const today = todayKey(new Date());
  const [seenIds, setSeenIds] = useState<string[]>(() => readIds(session, seenKey));
  const [hiddenIds, setHiddenIds] = useState<string[]>(() => readIds(device, hiddenKey));
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [localCounts, setLocalCounts] = useState<DayCounts>(() => readDayCounts(showsKey, today));

  const { data } = useQuery({
    queryKey: POPUP_QUERY_KEYS.active,
    queryFn: fetchActivePopups,
    staleTime: 5 * 60_000,
  });

  // A member is measured against the server's tally, a visitor against this
  // device's. The query is never invalidated after an impression is recorded, so
  // `data.impressions` is the tally as of lobby entry and the dialog on screen
  // cannot be pulled out from under the viewer by its own POST.
  const dayCounts = useMemo(
    () =>
      effectiveDayCounts({
        today,
        authed,
        local: localCounts,
        server: authed ? data?.impressions : undefined,
      }),
    [today, authed, localCounts, data],
  );

  const queue = useMemo(
    () => selectPopupsToShow(data?.items ?? [], { authed, seenIds: [...seenIds, ...hiddenIds], dayCounts }),
    [data, authed, seenIds, hiddenIds, dayCounts],
  );
  const current = queue[0];
  const currentId = current?.id;

  // The impression is counted when the dialog actually reaches the screen, not
  // when it is dismissed — a visitor who reloads the page instead of closing the
  // popup has still seen it, and an uncounted reload is an uncapped popup.
  //
  // Counting inside an effect would re-enter the memo above and drop `current`
  // mid-render, so the write is deliberately kept out of React state: the ref
  // guard also absorbs StrictMode's double-invoke, which shares the instance.
  //
  // A member's impression also goes to svc-notify, which is what makes the cap
  // survive a cache clear or a second device. It is fire-and-forget: the popup
  // is already on screen, and a failed write costs at most one uncounted
  // impression — the pre-#231 behaviour, not a worse one. The device record is
  // still written for a member, because it is the tally that takes over the
  // moment that member signs out in this browser.
  const countedRef = useRef<Set<string>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!currentId || countedRef.current.has(currentId)) return;
    countedRef.current.add(currentId);
    if (authed) void recordPopupImpression(currentId).catch(() => {});
    const next = recordShow(readDayCounts(showsKey, today), currentId);
    try {
      localStorage.setItem(showsKey, JSON.stringify(next));
    } catch {
      // storage unavailable — the cap degrades to session-only, as before #231
    }
  }, [currentId, showsKey, today, authed]);

  if (!current) return null;

  const dismiss = () => {
    const next = [...seenIds, current.id];
    setSeenIds(next);
    setLocalCounts(readDayCounts(showsKey, today));
    writeIds(session, seenKey, next);
    if (dontShowAgain) {
      const hidden = [...hiddenIds, current.id];
      setHiddenIds(hidden);
      writeIds(device, hiddenKey, hidden);
      setDontShowAgain(false);
    }
  };

  // Rendered at its own size (never upscaled, never cropped) up to the viewport,
  // with no frame — creatives are often cut-out art on a transparent background.
  const creative = (
    <img
      src={current.imageUrl}
      alt={current.name}
      className="block h-auto w-auto max-h-[75dvh] max-w-full object-contain"
    />
  );

  return (
    <Dialog open onOpenChange={(open) => !open && dismiss()}>
      <DialogContent
        ref={contentRef}
        hideClose
        // Focus lands on the dialog itself rather than the X, so a popup does
        // not open wearing a focus ring; Tab still reaches the X first.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
        className="max-w-3xl items-center gap-3 overflow-visible border-0 bg-transparent p-0 shadow-none"
      >
        <DialogTitle className="sr-only">{current.name}</DialogTitle>
        {/* Shrinks to the creative, so the X sits on the art's top-right corner. */}
        <div className="flex max-w-full flex-col items-end gap-1">
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('common.close')}
            className="grid h-11 w-11 place-items-center rounded-full text-white drop-shadow-[0_2px_6px_rgba(0,0,0,.7)] transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          >
            <Icon name="close" size={30} />
          </button>
          {current.linkUrl ? (
            <a href={current.linkUrl} target="_blank" rel="noopener noreferrer" className="block max-w-full">
              {creative}
            </a>
          ) : (
            creative
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2.5 text-[15px] text-white drop-shadow-[0_1px_4px_rgba(0,0,0,.8)]">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="h-5 w-5 accent-[var(--primary)]"
          />
          <span>{t('popups.dontShowAgain')}</span>
        </label>
      </DialogContent>
    </Dialog>
  );
}
